import { lazy, useEffect } from "react"
import ScoutingMainLayout from "layout/scouting/MainLayout"
import ScoutingCommonLayout from "layout/scouting/CommonLayout"
import Loadable from "components/Loadable"
import AuthGuard from "utils/route-guard/AuthGuard"
import { UserProfile, UserRoles } from "types/auth"
import AnalyticsScoutLogsPage from "pages/scouting/admin/analytics-scout-logs/AnalyticsScoutLogs"

const MaintenanceError = Loadable(lazy(() => import("pages/maintenance/error/404")))
const MaintenanceError500 = Loadable(lazy(() => import("pages/maintenance/error/500")))
const MaintenanceUnderConstruction = Loadable(
  lazy(() => import("pages/maintenance/under-construction/under-construction"))
)
const MaintenanceComingSoon = Loadable(lazy(() => import("pages/maintenance/coming-soon/coming-soon")))

const PlaylistPage = Loadable(lazy(() => import("pages/scouting/playlist/PlaylistPage")))
const PlaylistDetail = Loadable(lazy(() => import("pages/scouting/playlist/PlaylistDetail")))
const TracksPage = Loadable(lazy(() => import("pages/scouting/tracks/TracksPage")))
const ArtistsPage = Loadable(lazy(() => import("pages/scouting/artists/ArtistsPage")))
const SpotifyArtistRequestPage = Loadable(
  lazy(() => import("pages/scouting/spotify-artist-request/SpotifyArtistRequestPage"))
)
const ArtistsDetail = Loadable(lazy(() => import("pages/scouting/artists/ArtistDetail")))
const AlbumsPage = Loadable(lazy(() => import("pages/scouting/albums/AlbumsPage")))
const AlbumDetail = Loadable(lazy(() => import("pages/scouting/albums/AlbumDetail")))
const ArtistAnalysisPage = Loadable(lazy(() => import("pages/scouting/artists/analysis/ArtistAnalysisPage")))
const BlacklistPage = Loadable(lazy(() => import("pages/scouting/blacklist/BlacklistPage")))
const OffersReports = Loadable(lazy(() => import("pages/scouting/offers-reports/OffersReports")))
const ConfirmedOffersPage = Loadable(lazy(() => import("pages/scouting/confirmed-offers/ConfirmedOffersPage")))
const ConfirmedOfferDetail = Loadable(lazy(() => import("pages/scouting/confirmed-offers/detail/ConfirmedOfferDetail")))
const DealOffersReportPage = Loadable(
  lazy(() => import("pages/scouting/confirmed-offers/deal-offers/DealOffersReportPage"))
)
const CompanyBreakdownPage = Loadable(lazy(() => import("pages/scouting/company-breakdown/CompanyBreakdownPage")))
const CompanyPage = Loadable(lazy(() => import("pages/scouting/company/CompanyPage")))
const AffiliatePage = Loadable(lazy(() => import("pages/scouting/affiliate/AffiliatePage")))
const SystemSettings = Loadable(lazy(() => import("pages/scouting/admin/system/SystemSettings")))
const TaskStatuses = Loadable(lazy(() => import("pages/scouting/admin/task-statuses/TaskStatuses")))
const PipelineActions = Loadable(lazy(() => import("pages/scouting/admin/pipeline-actions/PipelineActions")))
const SchedulerLog = Loadable(lazy(() => import("pages/scouting/admin/scheduler/SchedulerLog")))
const AllowedOfferLinks = Loadable(lazy(() => import("pages/scouting/admin/allowed-offer-links/AllowedOfferLinks")))
const UsersPage = Loadable(lazy(() => import("pages/scouting/admin/user-list/UsersPage")))
const AnalyticsUsersLogs = Loadable(lazy(() => import("pages/scouting/admin/analytics-user-logs/AnalyticsUserLogs")))
const AnalyticsScoutLogs = Loadable(lazy(() => import("pages/scouting/admin/analytics-scout-logs/AnalyticsScoutLogs")))
const TagsPage = Loadable(lazy(() => import("pages/scouting/admin/tags/TagsPage")))
const UnitPricePage = Loadable(lazy(() => import("pages/scouting/admin/unit-price/UnitPricePage")))
const ArtistPipelineDetail = Loadable(lazy(() => import("pages/scouting/artists/pipeline/ArtistPipelineDetail")))
const CompanyBreakdownDetail = Loadable(lazy(() => import("pages/scouting/company-breakdown/CompanyBreakdownDetail")))
const TasksPage = Loadable(lazy(() => import("pages/scouting/task/Tasks")))
const InboxPage = Loadable(lazy(() => import("pages/scouting/inbox/Inbox")))
const UnitPriceDashboard = Loadable(lazy(() => import("pages/scouting/unit-price-dashboard/UnitPriceDashboard")))
const ReportUploadColumnPage = Loadable(
  lazy(() => import("pages/scouting/admin/report-upload-column/ReportUploadColumnPage"))
)
const MessageContent = Loadable(lazy(() => import("pages/scouting/admin/message-content/MessageContent")))
const DistributorsPage = Loadable(lazy(() => import("pages/scouting/admin/distributors/DistributorsPage")))
const InstagramAccountsPage = Loadable(lazy(() => import("pages/scouting/admin/instagram-accounts/InstagramAccounts")))
const OfferVariationTemplatesPage = Loadable(
  lazy(() => import("pages/scouting/admin/offer-variation-templates/OfferVariationTemplatesPage"))
)
const ReportUploadRequestsPage = Loadable(
  lazy(() => import("pages/scouting/report-upload-requests/ReportUploadRequestsPage"))
)
const PreRegistrationsPage = Loadable(lazy(() => import("pages/scouting/pre-registrations/PreRegistrationsPage")))

const scoutingChildren = [
  {
    path: "home",
    element: <div>WELCOME TO SCOUTING</div>
  },
  {
    path: "playlist",
    element: <PlaylistPage />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "tracks",
    element: <TracksPage showFilter={true} showLabelColumns={true} showOwnerships={false} defaultLimit={10} />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "artists",
    element: <ArtistsPage />,
    roleIds: [
      UserRoles.SCOUTING_ADMIN,
      UserRoles.SCOUTING_ACCESS,
      UserRoles.ACCOUNT,
      UserRoles.OUTREACH,
      UserRoles.OUTREACH_DATA_OPS
    ]
  },
  {
    path: "playlist/:playlistId",
    element: <PlaylistDetail />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "artists/:artistIdParam",
    element: <ArtistsDetail showAlbums={true} showConfirmedOffers={true} />,
    roleIds: [
      UserRoles.SCOUTING_ADMIN,
      UserRoles.SCOUTING_ACCESS,
      UserRoles.ACCOUNT,
      UserRoles.OUTREACH,
      UserRoles.OUTREACH_DATA_OPS
    ]
  },
  {
    path: "artists/:artistId/analysis/:analysisId",
    element: <ArtistAnalysisPage />,
    roleIds: [
      UserRoles.SCOUTING_ADMIN,
      UserRoles.SCOUTING_ACCESS,
      UserRoles.ACCOUNT,
      UserRoles.OUTREACH,
      UserRoles.OUTREACH_DATA_OPS
    ]
  },
  {
    path: "pipeline/:artistIdParam",
    element: <ArtistPipelineDetail />,
    roleIds: [
      UserRoles.SCOUTING_ADMIN,
      UserRoles.SCOUTING_ACCESS,
      UserRoles.ACCOUNT,
      UserRoles.OUTREACH,
      UserRoles.OUTREACH_DATA_OPS
    ]
  },
  {
    path: "releases",
    element: <AlbumsPage artistIdProp={0} defaultLimit={10} />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "releases/:albumId",
    element: <AlbumDetail />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "blacklist",
    element: <BlacklistPage />,
    roleIds: [UserRoles.BLACK_LIST_VALIDATOR]
  },
  {
    path: "tasks",
    element: <TasksPage />
  },
  {
    path: "inbox",
    element: <InboxPage />
  },
  {
    path: "offers-reports",
    element: <OffersReports />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "unit-price-dashboard",
    element: <UnitPriceDashboard />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    path: "spotify-artist-request",
    element: <SpotifyArtistRequestPage />,
    roleIds: [UserRoles.SCOUTING_ADMIN]
  },
  {
    path: "pre-registrations",
    element: <PreRegistrationsPage />,
    roleIds: [UserRoles.SCOUTING_ADMIN]
  },
  {
    path: "confirmed-offers",
    element: <ConfirmedOffersPage />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS, UserRoles.BUYOUT_OFFER]
  },
  {
    path: "deals",
    element: <ConfirmedOffersPage isDealOffers={true} />,
    roleIds: [UserRoles.SCOUTING_ADMIN]
  },
  {
    path: "deals-report",
    element: <DealOffersReportPage />,
    roleIds: [UserRoles.SCOUTING_ADMIN]
  },
  {
    path: "confirmed-offer/:confirmedOfferId",
    element: <ConfirmedOfferDetail isPublic={false} />,
    roleIds: [UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS, UserRoles.BUYOUT_OFFER]
  },
  {
    path: "scout",
    element: <AffiliatePage />,
    roleIds: [UserRoles.SCOUTING_ADMIN]
  },
  {
    path: "company",
    element: <CompanyPage />,
    roleIds: [UserRoles.BLACK_LIST_VALIDATOR]
  },
  {
    path: "company-breakdown",
    element: <CompanyBreakdownPage blackListPage={false} />,
    roleIds: [UserRoles.BLACK_LIST_VALIDATOR]
  },
  {
    path: "company-breakdown-detail/:pLabel",
    element: <CompanyBreakdownDetail />,
    roleIds: [UserRoles.BLACK_LIST_VALIDATOR]
  },
  {
    path: "report-upload-requests",
    element: <ReportUploadRequestsPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN, UserRoles.SCOUTING_ADMIN, UserRoles.SCOUTING_ACCESS]
  },
  {
    // open to every user for their own appearance setting; global settings stay super admin only
    path: "admin/system-settings",
    element: <SystemSettings />
  },
  {
    path: "admin/task-statuses",
    element: <TaskStatuses />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/pipeline-actions",
    element: <PipelineActions />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/scheduler-logs",
    element: <SchedulerLog />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/allowed-offer-links",
    element: <AllowedOfferLinks />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/tags",
    element: <TagsPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/report-upload-columns",
    element: <ReportUploadColumnPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/unit-price",
    element: <UnitPricePage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/users",
    element: <UsersPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/analytics-user-logs",
    element: <AnalyticsUsersLogs />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/analytics-scout-logs",
    element: <AnalyticsScoutLogs />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/message-content",
    element: <MessageContent />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/distributors",
    element: <DistributorsPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/instagram-accounts",
    element: <InstagramAccountsPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  },
  {
    path: "admin/offer-variation-templates",
    element: <OfferVariationTemplatesPage />,
    roleIds: [UserRoles.SCOUTING_SUPER_ADMIN]
  }
]

const filterChildrenByRole = (children: any[], user: UserProfile | null | undefined) => {
  const userRoleIds: number[] = user?.role_ids ?? []

  return children.filter((route) => {
    const permitted = route.roleIds as number[] | undefined

    if (!permitted || permitted.length === 0) return true

    if (!userRoleIds.length) return false

    return permitted.some((r) => userRoleIds.includes(r))
  })
}

export const buildScoutingRoutes = (user: UserProfile | null | undefined) => {
  const filteredChildren = filterChildrenByRole(scoutingChildren, user)

  return {
    path: "/",
    children: [
      {
        path: "/",
        element: (
          <AuthGuard>
            <ScoutingMainLayout />
          </AuthGuard>
        ),
        children: filteredChildren
      },
      {
        path: "/maintenance",
        element: <ScoutingCommonLayout />,
        children: [
          {
            path: "404",
            element: <MaintenanceError />
          },
          {
            path: "500",
            element: <MaintenanceError500 />
          },
          {
            path: "under-construction",
            element: <MaintenanceUnderConstruction />
          },
          {
            path: "coming-soon",
            element: <MaintenanceComingSoon />
          }
        ]
      }
    ]
  }
}

export default buildScoutingRoutes
